import { Table, Tag, Button } from 'antd';
import moment from 'moment';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

const SOURCE_IDS = {
    B2B: '7c8f3009-d244-4ee8-8a44-b71acf5af159',
    DIGITAL: 'c43d9c47-2734-4c29-a08d-a50442a89aaf'
};

const CommodityComparisonTable = ({ dailyData = [], monthlyData = [], currentContactStore, filterMonth, filterYear, filterText = '' }) => {
    const history = useHistory();
    const formatNumber = (number) => {
        if (number === undefined || number === null) return '0,00';
        return number
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
            .replace('.', ',');
    };

    const getStatusTag = (status) => {
        if (!status) return <Tag style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}>НЕМАЄ ДАНИХ</Tag>;
        const statusUpper = status.toUpperCase();
        switch (statusUpper) {
            case 'ЗАТВЕРДЖЕНО':
            case 'ПЕРЕВІРЕНО':
            case 'APPROVED':
                return <Tag color="green" style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}>ЗАТВЕРДЖЕНО</Tag>;
            case 'ПОДАНО':
            case 'SUBMITTED':
                return <Tag color="gold" style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}>ПОДАНО</Tag>;
            case 'Є РОЗБІЖНОСТІ':
            case 'DISCREPANCY':
                return <Tag color="red" style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}>Є РОЗБІЖНОСТІ</Tag>;
            default:
                return <Tag style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}>{statusUpper}</Tag>;
        }
    };

    const columns = [
        {
            title: 'Звітний період',
            dataIndex: 'period',
            key: 'period',
            className: 'name',
            align: 'left',
            width: 250,
            onCell: () => ({ style: { verticalAlign: 'middle' } }),
            render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
        },
        {
            title: 'Місячний звіт B2B (з ПДВ)',
            dataIndex: 'b2bAmount',
            key: 'b2bAmount',
            className: 'excise-tax center',
            width: 250,
            onCell: () => ({ style: { verticalAlign: 'middle' } }),
            render: (val, record) => (
                <span
                    style={{ fontSize: 14, color: '#9A18FF', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                    onClick={() => history.push('/commodity-circulation?tab=tradeMonthly')}
                >
                    {formatNumber(val)} грн
                </span>
            )
        },
        {
            title: 'Цифровий ТО (з ПДВ)',
            dataIndex: 'digitalAmount',
            key: 'digitalAmount',
            className: 'excise-tax center',
            width: 250,
            onCell: () => ({ style: { verticalAlign: 'middle' } }),
            render: (val, record) => {
                if (!record.isApproved) return <span style={{ color: '#bfbfbf' }}>—</span>;
                return (
                    <span
                        style={{ fontSize: 14, color: '#9A18FF', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                        onClick={() => history.push('/commodity-circulation?tab=tradeReconciliation')}
                    >
                        {formatNumber(val)} грн
                    </span>
                );
            }
        },
        {
            title: 'Різниця',
            dataIndex: 'diff',
            key: 'diff',
            className: 'excise-tax center',
            width: 180,
            onCell: () => ({ style: { verticalAlign: 'middle' } }),
            render: (val, record) => {
                if (!record.b2bAmount || !record.isApproved) return <span style={{ color: '#bfbfbf' }}>—</span>;
                const color = Math.abs(val) > 0.01 ? '#ff4d4f' : 'inherit';
                return <span style={{ color, fontWeight: Math.abs(val) > 0.01 ? 700 : 400 }}>{formatNumber(val)} грн</span>;
            }
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            className: 'status-col',
            align: 'center',
            width: 220,
            onCell: () => ({ style: { verticalAlign: 'middle' } }),
            render: (status, record) => {
                if (!record.b2bAmount) return null;
                
                let displayStatus = 'Подано';
                let hasDiscrepancy = false;
                
                if (record.isApproved) {
                    if (Math.abs(record.diff) > 0.01) {
                        displayStatus = 'Є розбіжності';
                        hasDiscrepancy = true;
                    } else {
                        displayStatus = 'Затверджено';
                    }
                }
                
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        {getStatusTag(displayStatus)}
                {/* hasDiscrepancy && (
                            <Button 
                                type="primary" 
                                size="small"
                                onClick={() => history.push('/requests')}
                                style={{ 
                                    fontSize: '11px', 
                                    borderRadius: '4px',
                                    background: '#9A18FF',
                                    borderColor: '#9A18FF',
                                    fontWeight: 600,
                                    height: '28px',
                                    padding: '0 8px'
                                }}
                            >
                                Звернення на коригування
                            </Button>
                        ) */}
                    </div>
                );
            }
        }
    ];

    const tableData = useMemo(() => {
        const monthMap = {};

        // Process monthly B2B data
        monthlyData.forEach(item => {
            if (item.dataSource !== SOURCE_IDS.B2B || item.isOnlineSales) return;

            const mDate = moment(item.date);
            const monthKey = mDate.format('YYYY-MM');

            if (!monthMap[monthKey]) {
                monthMap[monthKey] = {
                    key: monthKey,
                    date: mDate,
                    period: `За ${mDate.format('MMMM YYYY')} р.`,
                    b2bAmount: 0,
                    digitalAmount: 0,
                    status: null,
                    isApproved: false
                };
            }
            monthMap[monthKey].b2bAmount += item.amountWithVAT || 0;
            monthMap[monthKey].status = item.status;
            if (item.turnoverApproving === true || item.turnoverApproving === 'true') {
                monthMap[monthKey].isApproved = true;
            }
        });

        // Process daily digital data
        dailyData.forEach(item => {
            if (item.dataSource !== SOURCE_IDS.DIGITAL) return;

            const dDate = moment(item.date);
            const monthKey = dDate.format('YYYY-MM');

            if (monthMap[monthKey] && monthMap[monthKey].isApproved) {
                monthMap[monthKey].digitalAmount += item.amountWithVAT || 0;
            }
        });

        let result = Object.values(monthMap).map(item => ({
            ...item,
            diff: item.b2bAmount - item.digitalAmount
        }));

        // Filter out months whose "completion date" (5th of the next month) hasn't passed
        const now = moment();
        result = result.filter(item => {
            const completionDate = item.date.clone().add(1, 'month').date(5);
            return now.isSameOrAfter(completionDate, 'day');
        });

        // Apply filters if present
        if (filterMonth || filterYear || filterText) {
            result = result.filter(item => {
                const m = item.date.month() + 1;
                const y = item.date.year();
                const matchesMonth = filterMonth ? m === filterMonth : true;
                const matchesYear = filterYear ? y === filterYear : true;

                const searchText = filterText.toLowerCase();
                const matchesText = filterText ? (
                    item.period.toLowerCase().includes(searchText) ||
                    (item.status && item.status.toLowerCase().includes(searchText))
                ) : true;

                return matchesMonth && matchesYear && matchesText;
            });
        }

        // Sort by date descending
        return result.sort((a, b) => b.date.valueOf() - a.date.valueOf());
    }, [dailyData, monthlyData, filterMonth, filterYear, filterText]);

    return (
        <Table
            className="default-table request-table"
            columns={columns}
            dataSource={tableData}
            pagination={false}
            locale={{ emptyText: 'Схоже, за вибраний період даних немає' }}
            rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
        />
    );
};

export default CommodityComparisonTable;
